import './style.css'
import * as THREE from 'three'
import { loadArenal } from './content/loadArenal'
import { loadPlay, resolveJourneyStops } from './content/loadPlay'
import { attractionsByHotspot, loadWorldContent } from './content/loadWorldContent'
import { latLonToWorld } from './world/geo'
import { createZoneHost } from './zone/zoneHost'
import type { ArenalWorld } from './world/createArenalWorld'
import type { Atmosphere, AtmosphereSettings } from './world/atmosphere'
import { createModeControls } from './world/controls'
import { createTrailGuide } from './world/trail'
import { createPlaceAudio } from './world/audio'
import type { TerrainFlightPath } from './world/flightPath'
import { createPostFx, type PostFx } from './world/postFx'
import { createPointsOfInterest, type PoiEntry } from './world/pointsOfInterest'
import { CATEGORY_LABEL } from './content/attractionMeta'
import {
  arrivalLine,
  greeting,
  questIntro,
  reviewNotice,
  speaker,
} from './content/narration'
import { bindAtlas } from './ui/atlas'
import { bindDialogue, type DialoguePage } from './ui/dialogue'
import { bindHud } from './ui/hud'
import { bindPhotoViewer } from './ui/photoViewer'
import { bindRoamTogether } from './ui/roamTogether'
import {
  discoveredCount,
  getRecord,
  isAssigned,
  loadQuestState,
  markAnnounced,
  markAssigned,
  markPhotographed,
  markSeen,
  markUnphotographed,
  photographedCount,
  saveQuestState,
  takeLegacyThumbs,
  type DiscoverableKind,
  type LegacyThumb,
} from './questState'
import { bindQuestChecklist } from './ui/questChecklist'
import {
  evaluateAll,
  evaluateQuest,
  isTraversalUnlocked,
  newlyCompleted,
} from './quests'
import { parseDeepLink, shareViewpointUrl, writeDeepLink } from './deepLink'
import { loadPlayState } from './playState'
import { track } from './analytics'
import {
  canvasToBlob,
  composePostcard,
  renderThumbnail,
  sharePhotoBlob,
  sharePostcard,
  slugify,
} from './photo'
import {
  deletePhoto,
  getFullBlob,
  putPhoto,
  requestPersistence,
} from './photoStore'
import type {
  Attraction,
  CameraMode,
  Hotspot,
  JourneyStop,
  Settlement,
  Viewpoint,
} from './types'

const canvas = document.querySelector<HTMLCanvasElement>('#world')!
const { hotspots, facts, viewpoints, place } = loadArenal()
const { players, journeys } = loadPlay()
const worldContent = loadWorldContent()
const questState = loadQuestState()
const playState = loadPlayState()
const initialLink = parseDeepLink()
const audio = createPlaceAudio()
const roam = bindRoamTogether()

const requestedJourney = journeys.find(
  (journey) => journey.id === (initialLink.journeyId ?? playState.journeyId),
)
let currentPlayer =
  players.find(
    (player) =>
      player.id ===
      (initialLink.playerId ?? requestedJourney?.playerIds[0] ?? playState.playerId),
  ) ?? players[0]!
let currentJourney =
  (requestedJourney?.playerIds.includes(currentPlayer.id) ? requestedJourney : undefined) ??
  journeys.find((journey) => journey.id === currentPlayer.defaultJourneyId) ??
  journeys[0]!
let hasPlaySelection = Boolean(
  initialLink.playerId ||
    initialLink.journeyId ||
    playState.playerId ||
    playState.journeyId,
)

let timeOfDay = 0.28
let holdGolden = 0
let validationMode = false
let currentView: Viewpoint | undefined =
  viewpoints.find((v) => v.id === place.signatureViewpointId) ?? viewpoints[0]
interface ArrivalState {
  elapsed: number
  duration: number
  from: THREE.Vector3
  to: THREE.Vector3
  path: TerrainFlightPath
  startTarget: THREE.Vector3
  target: THREE.Vector3
  onComplete: () => void
}
let arrival: ArrivalState | null = null
interface ActiveEncounter {
  hotspot: Hotspot
  stop: JourneyStop
  index: number
  total: number
}
let activeEncounter: ActiveEncounter | null = null
let nearestPoi: PoiEntry | null = null

/** Ground-zone walk-in: Parque Central / town centroid on the region map. */
const LA_FORTUNA_ENTER = { lat: 10.4709, lon: -84.6453 }
/** World units (~2.7 km). Focus must sit on the town, not merely see it. */
const LA_FORTUNA_ENTER_RADIUS = 30
const LA_FORTUNA_WALK_IDS = new Set(['la-fortuna', 'settlement-la-fortuna'])

function focusNearLaFortuna(): boolean {
  if (!controls) return false
  const focus = controls.getFocusPoint()
  const town = latLonToWorld(LA_FORTUNA_ENTER.lat, LA_FORTUNA_ENTER.lon)
  return Math.hypot(focus.x - town.x, focus.z - town.z) <= LA_FORTUNA_ENTER_RADIUS
}

/**
 * The region surface, built late.
 *
 * These are assigned in the boot block at the bottom of this file rather than
 * here, because a player arriving on foot needs none of them to take their
 * first step and the 44 km world costs 2.6 MB of code plus a second of mesh
 * building. Definite-assignment rather than nullable: every one of the forty
 * or so uses below runs from a callback that cannot fire before the region
 * exists, and making them all optional would be forty guards protecting
 * against nothing. The two places that genuinely can run earlier — zone entry
 * and the frame loop — check explicitly.
 */
let world!: ArenalWorld
let atmosphere!: Atmosphere
let postFx!: PostFx
let controls!: ReturnType<typeof createModeControls>
let poi!: ReturnType<typeof createPointsOfInterest>

const hotspotById = new Map(hotspots.map((hotspot) => [hotspot.id, hotspot]))
const stopByHotspotId = () =>
  new Map(currentJourney.stops.map((stop) => [stop.hotspotId, stop]))

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  powerPreference: 'high-performance',
})
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.12

// A tighter near plane keeps enough depth precision for screen-space occlusion.
const camera = new THREE.PerspectiveCamera(
  65,
  window.innerWidth / window.innerHeight,
  0.5,
  1400,
)

const hintFor = (mode: CameraMode) => {
  if (mode === 'explore') {
    if (window.matchMedia('(max-width: 720px)').matches) {
      return 'Drag map · pinch zoom · double-tap fly'
    }
    return 'Drag map · right-drag rotate · scroll/pinch zoom · double-click fly'
  }
  if (mode === 'walk') return 'Click to look · WASD move · Shift sprint'
  if (mode === 'flight') {
    return 'Drag or WASD steer · Shift boost · Esc to land'
  }
  return 'Explore Arenal'
}

let openPlayerChooser: () => void = () => {}

const hud = bindHud({
  onMode: (mode) => {
    if (
      mode === 'flight' &&
      !isTraversalUnlocked(worldContent.quests, questState, 'toucan-flight')
    ) {
      return
    }
    // Walk drops into the La Fortuna ground zone (the only on-foot pocket).
    // Regional first-person stays available via deep link / debug only.
    if (mode === 'walk') {
      enterLaFortunaTown()
      return
    }
    const pausedJourney = trail.active
    trail.stop()
    activeEncounter = null
    roam.close()
    controls.enabled = true
    controls.setMode(mode)
    if (mode === 'flight') {
      audio.playCue('arrival')
      track('flight_entered', { playerId: currentPlayer.id })
    }
    if (pausedJourney) {
      hud.setTrailStatus('')
      hud.flash('Guided trail paused')
    }
    writeDeepLink({
      viewId: currentView?.id,
      mode,
      playerId: currentPlayer.id,
      journeyId: currentJourney.id,
    })
  },
  onOverview: () => {
    const overview = viewpoints.find((view) => view.id === 'arenal-overview')
    if (overview) applyViewpoint(overview)
  },
  onNorth: () => controls.resetNorth(),
  onTop: () => controls.topDown(),
  onPlayer: () => openPlayerChooser(),
  onTrail: () => hud.flash('Guided tourism trails are retired — use the Atlas'),
  onAtlas: () => toggleAtlas(),
  onPoiPrompt: () => {
    // The prompt is the walk-in affordance for La Fortuna — not another fact card.
    if (canEnterLaFortuna()) {
      enterLaFortunaTown()
      return
    }
    openNearestPoi()
  },
  onGolden: () => triggerGoldenHour(),
  onPhoto: () => {
    // On foot the subject is whatever the camera is framing, which is how a
    // camera works and how you photograph a mountain you cannot walk to.
    const aimed = zoneHost.isActive() ? zoneHost.aim() : null
    if (aimed) return takePhoto(aimed.name, undefined, aimed.id)
    takePhoto(undefined, undefined, nearestPoi?.id)
  },
  onShare: () => void shareCurrentView(),
  onAudio: () => void toggleAudio(),
  onMapData: () => {
    validationMode = !validationMode
    world.setValidationMode(validationMode)
    hud.flash(validationMode ? 'Map validation on' : 'Map validation off')
    return validationMode
  },
})

const dialogue = bindDialogue({ onBlip: () => audio.playCue('type') })

const arriving = document.getElementById('arriving') as HTMLDivElement
const zoneBar = document.getElementById('zone-bar') as HTMLDivElement
const zoneBarPlace = document.getElementById('zone-bar-place') as HTMLParagraphElement
const zoneBarHint = document.querySelector('.zone-bar-hint') as HTMLParagraphElement
const zoneExit = document.getElementById('zone-exit') as HTMLButtonElement
const poiPrompt = document.getElementById('poi-prompt') as HTMLButtonElement
const poiPromptLabel = document.getElementById('poi-prompt-label') as HTMLSpanElement
const questChecklist = bindQuestChecklist()

/** A settled place is an area you are inside, not a pin you stand on. */
const TOWN_RADIUS_M = 400
/** A waterfall or a trailhead is a spot, and should be reached rather than neared. */
const SITE_RADIUS_M = 60

/**
 * How close counts as arriving.
 *
 * A single radius cannot serve both a hanging bridge and a town of 15,000.
 * Anything sharing coordinates with a settlement is that settlement under
 * another name — `la-fortuna` the attraction and `settlement-la-fortuna` are
 * the same square metre of Costa Rica — so it inherits the town's extent, and
 * standing in the plaza counts as having arrived in the town. Everything else
 * is a place you walk up to.
 */
function anchorRadius(location: { lat: number; lon: number }): number {
  const shared = worldContent.settlements.some(
    (town) =>
      Math.abs(town.location.lat - location.lat) < 0.0006 &&
      Math.abs(town.location.lon - location.lon) < 0.0006,
  )
  return shared ? TOWN_RADIUS_M : SITE_RADIUS_M
}

/**
 * Zone chrome: always allow return to the region map; quest copy stays in the
 * hint so the opening still has somewhere to live.
 */
function refreshZoneBar() {
  zoneExit.hidden = false
  const opening = worldContent.quests.find((quest) => quest.order === 1)
  questChecklist.refresh(worldContent.quests, questState)
  if (opening && !isAssigned(questState, opening.id)) {
    zoneBarHint.textContent =
      'Parque Central — WASD into the park · talk to Don Rafa · Esc for map'
    return
  }
  const remaining = opening
    ? evaluateQuest(opening, questState).steps.filter((step) => !step.done)
    : []
  zoneBarHint.textContent = remaining.length
    ? `${remaining[0]!.text} · Esc returns to the map`
    : 'WASD / click walk the park · Esc returns to the map'
}

const zoneHost = createZoneHost(renderer, {
  onEnter: (zone) => {
    // May run before the region is built: on foot, that is the normal case.
    if (controls) controls.enabled = false
    document.body.classList.add('on-foot')
    zoneBarPlace.textContent = zone.frame.name
    zoneBar.hidden = false
    refreshZoneBar()
    track('zone_entered', { id: zone.frame.id })
  },
  onExit: (from) => {
    controls.enabled = true
    document.body.classList.remove('on-foot')
    zoneBar.hidden = true
    questChecklist.hide()
    poiPrompt.classList.add('hidden')
    track('zone_exited', { lat: Number(from.lat.toFixed(4)) })

    // The first ascent is the reveal: street level to the whole 44 km in one
    // move. Dropping the player back onto whatever the camera happened to be
    // looking at would waste it.
    const firstAscent = !localStorage.getItem(ASCENDED_KEY)
    if (firstAscent) {
      const overview =
        viewpoints.find((view) => view.id === 'arenal-overview') ?? viewpoints[0]
      if (overview) applyViewpoint(overview)
      try {
        localStorage.setItem(ASCENDED_KEY, new Date().toISOString())
      } catch {
        // Cosmetic only; a lost flag just replays the overview next time.
      }
      hud.flash('Arenal, all of it')
    } else {
      hud.flash('Back on the map')
    }
  },
  onStatus: (message) => hud.flash(message),
  anchors: () => [
    ...worldContent.attractions.map((entry) => ({
      id: entry.id,
      kind: 'attraction' as const,
      name: entry.name,
      lat: entry.location.lat,
      lon: entry.location.lon,
      radiusM: anchorRadius(entry.location),
    })),
    ...worldContent.settlements.map((entry) => ({
      id: entry.id,
      kind: 'settlement' as const,
      name: entry.name,
      lat: entry.location.lat,
      lon: entry.location.lon,
      radiusM: TOWN_RADIUS_M,
    })),
  ],
  onDiscover: (anchor) => {
    if (!markSeen(questState, anchor.id, anchor.kind)) return
    saveQuestState(questState)
    audio.playCue('discover')
    const line = arrivalLine(anchor.id)
    hud.flash(line ? `${anchor.name} — ${line}` : `Found ${anchor.name}`)
    hud.setProgress(discoveredCount(questState), totalDiscoverables)
    syncQuests()
    refreshZoneBar()
  },
  onArrive: () => greetInZone(),
})

function canEnterLaFortuna(): boolean {
  if (zoneHost.isActive()) return false
  if (LA_FORTUNA_WALK_IDS.has(nearestPoi?.id ?? '')) return true
  return focusNearLaFortuna()
}

function enterLaFortunaTown() {
  if (zoneHost.isActive()) return
  void zoneHost.enter('la-fortuna')
}

zoneExit?.addEventListener('click', () => zoneHost.exit())
poiPrompt.addEventListener('click', () => {
  const npc = zoneHost.nearbyNpc()
  if (npc) talkToNpc(npc)
})

const hotspotAttractions = attractionsByHotspot()
const totalDiscoverables =
  worldContent.attractions.length +
  worldContent.settlements.length +
  worldContent.history.length +
  hotspots.filter((hotspot) => !hotspotAttractions.has(hotspot.id)).length
const atlas = bindAtlas({
  attractions: worldContent.attractions,
  settlements: worldContent.settlements,
  history: worldContent.history,
  quests: worldContent.quests,
  questProgress: () => evaluateAll(worldContent.quests, questState),
  getRecord: (id) => getRecord(questState, id),
  counts: () => ({
    discovered: discoveredCount(questState),
    photographed: photographedCount(questState),
    total: totalDiscoverables,
  }),
  onFocus: (target) => flyToLocation(target),
  onPhotoOpen: (id, title) => {
    track('photo_viewed', { id })
    photoViewer.open(id, title)
  },
  onTab: (tab) => track('atlas_opened', { tab, playerId: currentPlayer.id }),
  onExpand: (kind, id) => {
    track('atlas_entry_read', { kind, id })
    if (kind !== 'history') return
    if (!markSeen(questState, id, 'history')) return
    saveQuestState(questState)
    hud.setProgress(discoveredCount(questState), totalDiscoverables)
    syncQuests()
  },
  onClose: () => hud.setAtlasActive(false),
})

const photoViewer = bindPhotoViewer({
  onDelete: (id) => {
    void deletePhoto(id).then(() => {
      markUnphotographed(questState, id)
      saveQuestState(questState)
      track('photo_deleted', { id })
      syncQuests()
      if (atlas.isOpen()) atlas.open()
    })
  },
  onShare: (id) => {
    void getFullBlob(id).then(async (blob) => {
      if (!blob) {
        hud.flash('Could not share the photo')
        return
      }
      const subject = subjectForDiscoverable(id)
      const outcome = await sharePhotoBlob(
        blob,
        `play-costa-rica-${slugify(subject)}.jpg`,
        `${place.name} — ${subject}`,
      )
      if (outcome === 'shared') hud.flash('Photo shared')
      else if (outcome === 'downloaded') hud.flash('Photo saved')
      else hud.flash('Could not share the photo')
    })
  },
})

const ELDER_GREETED_KEY = 'roam:arenal:world:greeted'

function elderPages(body: string[], title = speaker().name): DialoguePage[] {
  return body.map((text, index) => ({
    kicker: index === 0 ? speaker().role : undefined,
    title: index === 0 ? title : undefined,
    body: text,
  }))
}

/** True while a guided sequence owns the screen and must not be interrupted. */
function sceneIsBusy(): boolean {
  return trail.active || roam.isOpen() || dialogue.isOpen()
}

const ZONE_GREETED_KEY = 'roam:arenal:zone:greeted'
const ASCENDED_KEY = 'roam:arenal:ascended'

/**
 * Boot nudge only — Don Rafa's speech is walk-up now, and he assigns the
 * opening quest when that talk finishes.
 */
function greetInZone() {
  if (sceneIsBusy()) return
  const opening = worldContent.quests.find((quest) => quest.order === 1)
  if (opening && !isAssigned(questState, opening.id)) {
    hud.flash('Talk to Don Rafa in the plaza')
    audio.playCue('arrival')
  } else if (!localStorage.getItem(ZONE_GREETED_KEY)) {
    hud.flash('La Fortuna — on foot')
  }
  try {
    localStorage.setItem(ZONE_GREETED_KEY, new Date().toISOString())
  } catch {
    // Without storage the nudge simply repeats.
  }
  refreshZoneBar()
}

/** Walk-up talk with an in-world NPC. Don Rafa assigns the opening quest. */
function talkToNpc(npc: {
  id: string
  name: string
  prompt: string
  assignsQuestId?: string
}) {
  if (dialogue.isOpen() || sceneIsBusy()) return
  const assigns = npc.assignsQuestId
  const firstTalk = npc.id === 'don-rafa' && !localStorage.getItem(ELDER_GREETED_KEY)
  const intro = assigns ? questIntro(assigns) : undefined

  let pages: DialoguePage[]
  if (firstTalk) {
    pages = elderPages([...greeting().pages, ...(intro ? [intro] : [])])
    pages.push({ kicker: speaker().role, body: reviewNotice(), chip: 'Guide' })
  } else if (assigns && !isAssigned(questState, assigns)) {
    pages = elderPages([
      'Still here, then. Good.',
      ...(intro ? [intro] : ['Go on — the streets will tell you more than I will.']),
    ])
  } else {
    pages = elderPages([
      'So you came back down. Good. From up there it is a postcard; down here it is a town.',
    ])
  }

  dialogue.open(pages, {
    onClose: () => {
      if (assigns && markAssigned(questState, assigns)) {
        saveQuestState(questState)
        hud.flash(`Quest assigned — ${worldContent.quests.find((q) => q.id === assigns)?.title ?? assigns}`)
        audio.playCue('discover')
      }
      try {
        const now = new Date().toISOString()
        if (npc.id === 'don-rafa') localStorage.setItem(ELDER_GREETED_KEY, now)
        localStorage.setItem(ZONE_GREETED_KEY, now)
      } catch {
        // Greeting flags are convenience only.
      }
      refreshZoneBar()
    },
  })
  audio.playCue('arrival')
}

function refreshNpcPrompt() {
  if (!zoneHost.isActive() || dialogue.isOpen() || atlas.isOpen()) {
    poiPrompt.classList.add('hidden')
    return
  }
  const npc = zoneHost.nearbyNpc()
  if (!npc) {
    poiPrompt.classList.add('hidden')
    return
  }
  poiPromptLabel.textContent = npc.prompt
  poiPrompt.classList.remove('hidden')
}

function syncQuests() {
  for (const quest of newlyCompleted(worldContent.quests, questState)) {
    // Opening quest stays incomplete in the UI until Don Rafa assigns it —
    // discoveries made early still count once the quest is active.
    if (!isAssigned(questState, quest.id)) continue
    audio.playCue('react')
    markAnnounced(questState, quest.id)
    saveQuestState(questState)
    track('quest_completed', { questId: quest.id, reward: quest.reward.value })
    const next = worldContent.quests.find((entry) => entry.order === quest.order + 1)
    if (quest.reward.value === 'toucan-flight') {
      // Toucan unlock only — no passport stamp / day-plan / partner funnel.
      audio.playCue('stamp')
      hud.flash(quest.reward.text)
    }
    if (sceneIsBusy()) {
      hud.flash(quest.reward.text)
    } else {
      const said = [`${quest.title}. Done.`, quest.reward.text]
      if (zoneHost.isActive() && quest.reward.value === 'toucan-flight') {
        said.push('Go on. Get up there and see the shape of the whole thing.')
      }
      const handover = next ? questIntro(next.id) : undefined
      if (handover) said.push(handover)
      dialogue.open(elderPages(said))
    }
  }
  if (zoneHost.isActive()) refreshZoneBar()
  hud.setFlightAvailable(
    isTraversalUnlocked(worldContent.quests, questState, 'toucan-flight'),
  )
}

/**
 * Attractions are authored in WGS84, so the atlas shares the same transform as
 * the terrain rather than carrying its own scene coordinates.
 */
function flyToLocation(target: {
  name: string
  location: { lat: number; lon: number }
  tier?: 'major' | 'minor'
}) {
  const { x, z } = latLonToWorld(target.location.lat, target.location.lon)
  const point = new THREE.Vector3(x, world.getHeight(x, z), z)
  arrival = null
  activeEncounter = null
  trail.stop()
  dialogue.close()
  roam.close()
  controls.enabled = true
  if (controls.mode !== 'explore') controls.setMode('explore')
  world.setArrivalProgress(1)
  controls.frameOn(point, target.tier === 'major' ? 190 : 110)
  hud.flash(target.name)
  track('attraction_focused', {
    name: target.name,
    tier: target.tier,
    playerId: currentPlayer.id,
  })
}

function toggleAtlas() {
  if (atlas.isOpen()) {
    atlas.close()
    hud.setAtlasActive(false)
    return
  }
  dialogue.close()
  atlas.open()
  hud.setAtlasActive(true)
}

function registerDiscovery(hotspotId: string) {
  const attraction = hotspotAttractions.get(hotspotId)
  const id = attraction?.id ?? hotspotId
  const kind: DiscoverableKind = attraction ? 'attraction' : 'hotspot'
  if (!markSeen(questState, id, kind)) return
  saveQuestState(questState)
  const found = discoveredCount(questState)
  hud.setProgress(found, totalDiscoverables)
  hud.flash(`Discovered · ${found}/${totalDiscoverables}`)
  track('landmark_discovered', {
    hotspotId,
    playerId: currentPlayer.id,
    journeyId: currentJourney.id,
  })
  syncQuests()
}

function attractionPages(attraction: Attraction, lead?: string): DialoguePage[] {
  const chip = attraction.needsVerification ? 'Unverified' : undefined
  const pages: DialoguePage[] = [
    {
      kicker: CATEGORY_LABEL[attraction.category],
      title: attraction.name,
      body: lead ?? attraction.summary,
      chip,
      action: {
        label: 'Photograph',
        onClick: () => takePhoto(attraction.name, undefined, attraction.id),
      },
    },
  ]
  if (lead && attraction.summary !== lead) {
    pages.push({ kicker: 'In short', title: attraction.name, body: attraction.summary })
  }
  if (attraction.howItWorks) {
    pages.push({ kicker: 'How it works', title: attraction.name, body: attraction.howItWorks })
  }
  if (attraction.discover?.length) {
    pages.push({
      kicker: 'On the ground',
      title: attraction.name,
      bulletsLabel: 'What you will notice',
      bullets: attraction.discover,
    })
  }
  return pages
}

function settlementPages(settlement: Settlement): DialoguePage[] {
  return [
    {
      kicker: settlement.kind === 'relocated' ? 'Relocated town' : 'Settlement',
      title: settlement.name,
      body: settlement.summary,
      chip: settlement.needsVerification ? 'Unverified' : undefined,
    },
  ]
}

function openDialoguePages(pages: DialoguePage[]) {
  atlas.close()
  audio.setDucked(true)
  audio.playCue('discover')
  dialogue.open(pages, { onClose: () => audio.setDucked(false) })
}

function openPoi(entry: PoiEntry) {
  if (LA_FORTUNA_WALK_IDS.has(entry.id)) {
    enterLaFortunaTown()
    return
  }
  const pages = entry.attraction
    ? attractionPages(entry.attraction)
    : entry.settlement
      ? settlementPages(entry.settlement)
      : []
  if (pages.length === 0) return
  if (markSeen(questState, entry.id, entry.kind)) {
    saveQuestState(questState)
    hud.setProgress(discoveredCount(questState), totalDiscoverables)
    syncQuests()
  }
  openDialoguePages(pages)
  track('poi_opened', {
    poiId: entry.id,
    kind: entry.kind,
    tier: entry.tier,
    playerId: currentPlayer.id,
  })
}

function openHotspot(hotspotId: string) {
  // The authored gold pin for the town is the walk-in, not a fact card.
  if (hotspotId === 'fortuna-look') {
    enterLaFortunaTown()
    return
  }
  const hotspot = hotspotById.get(hotspotId)
  if (!hotspot) return
  registerDiscovery(hotspotId)
  const lead = facts[hotspot.factId]?.plainAnswer
  const attraction = hotspotAttractions.get(hotspotId)
  openDialoguePages(
    attraction
      ? attractionPages(attraction, lead)
      : [{ kicker: 'Discovery', title: hotspot.title, body: lead ?? '' }],
  )
  track('poi_opened', { poiId: hotspotId, kind: 'hotspot', playerId: currentPlayer.id })
}

function openNearestPoi() {
  if (nearestPoi) openPoi(nearestPoi)
}

const trail = createTrailGuide(
  camera,
  hotspots,
  // Indirect, because this is built before the region is. A journey only ever
  // runs on the map, so by the time it samples the ground the world exists.
  (x, z) => world.getHeight(x, z),
  (hotspotId) => {
    const hotspot = hotspotById.get(hotspotId)
    const stop = stopByHotspotId().get(hotspotId)
    if (!hotspot || !stop) {
      trail.continue()
      return
    }
    activeEncounter = {
      hotspot,
      stop,
      index: trail.index,
      total: trail.total,
    }
    audio.setDucked(false)
    roam.openMission({
      stop,
      hotspot,
      index: trail.index,
      total: trail.total,
      onConfirm: resolveActiveEncounter,
    })
  },
  () => {
    activeEncounter = null
    audio.playCue('react')
    controls.enabled = true
    controls.setMode('explore')
    hud.setTrailStatus('')
    hud.flash('Trail complete — keep exploring')
    writeDeepLink({
      viewId: currentView?.id,
      mode: 'explore',
      playerId: currentPlayer.id,
      journeyId: currentJourney.id,
    })
  },
)

function resolveActiveEncounter() {
  const encounter = activeEncounter
  if (!encounter) return
  const { hotspot, stop, index, total } = encounter
  registerDiscovery(hotspot.id)
  track('encounter_found', {
    hotspotId: hotspot.id,
    playerId: currentPlayer.id,
    journeyId: currentJourney.id,
    index,
  })
  audio.setDucked(true)
  roam.openStop({
    journey: currentJourney,
    stop,
    hotspot,
    fact: facts[hotspot.factId],
    index,
    total,
    // The world reaction is the answer, so it is held back until the player
    // has committed to a guess.
    onReveal: (guess) => {
      if (stop.reactionId) world.react(stop.reactionId)
      audio.playCue('discover')
      if (guess) {
        track('prediction_made', {
          hotspotId: hotspot.id,
          playerId: currentPlayer.id,
          journeyId: currentJourney.id,
          choice: guess.index,
          correct: guess.correct,
        })
      }
    },
    onPhoto: () =>
      takePhoto(
        hotspot.title,
        stop.photoPrompt,
        hotspotAttractions.get(hotspot.id)?.id,
      ),
    onContinue: (nextHotspotId) => {
      if (nextHotspotId) {
        track('route_chosen', {
          fromHotspotId: hotspot.id,
          nextHotspotId,
          playerId: currentPlayer.id,
          journeyId: currentJourney.id,
        })
      }
      activeEncounter = null
      audio.setDucked(false)
      trail.continue(nextHotspotId)
    },
  })
}

trail.setStops(resolveJourneyStops(currentJourney, hotspots))

openPlayerChooser = () => {
  // Tourism persona picker retired — Atlas is the journal now.
  hud.flash('Open the Atlas for places and quests')
  toggleAtlas()
}

/** Single way back to free movement from any guided state. */
function exitToExplore(message?: string) {
  trail.stop()
  activeEncounter = null
  roam.close()
  dialogue.close()
  controls.enabled = true
  controls.setMode('explore')
  hud.setTrailStatus('')
  if (message) hud.flash(message)
  writeDeepLink({
    viewId: currentView?.id,
    mode: 'explore',
    playerId: currentPlayer.id,
    journeyId: currentJourney.id,
  })
}

function applyViewpoint(view: Viewpoint, { link = true } = {}) {
  arrival = null
  activeEncounter = null
  world.setArrivalProgress(1)
  currentView = view
  timeOfDay = view.timeOfDay
  world.setTimeOfDay(timeOfDay)
  const pos = new THREE.Vector3(...view.position)
  const target = new THREE.Vector3(...view.target)
  trail.stop()
  roam.close()
  // The map pose is still worth setting while the player is in a zone — it is
  // what they ascend into — but the map's own controls must stay asleep until
  // they are actually on it.
  controls.enabled = !zoneHost.isActive()
  if (view.mode === 'walk') {
    controls.setMode('walk')
    camera.position.copy(pos)
    camera.lookAt(target)
  } else {
    controls.setMode('explore')
    controls.setExplorePose(pos, target)
  }
  if (!link) return
  writeDeepLink({
    viewId: view.id,
    mode: controls.mode,
    ...(hasPlaySelection
      ? { playerId: currentPlayer.id, journeyId: currentJourney.id }
      : {}),
  })
}

function triggerGoldenHour() {
  timeOfDay = 0.22
  holdGolden = 8
  world.setTimeOfDay(timeOfDay)
  const signature =
    viewpoints.find((v) => v.id === currentJourney.completionViewpointId) ??
    viewpoints.find((v) => v.id === 'arenal-sunrise') ??
    viewpoints.find((v) => v.id === place.signatureViewpointId) ??
    viewpoints[0]
  if (signature && !trail.active) {
    currentView = signature
    if (controls.mode === 'walk' || controls.mode === 'flight') {
      controls.setMode('explore')
    }
    controls.setExplorePose(
      new THREE.Vector3(...signature.position),
      new THREE.Vector3(...signature.target),
    )
    writeDeepLink({
      viewId: signature.id,
      mode: controls.mode,
      playerId: currentPlayer.id,
      journeyId: currentJourney.id,
    })
  }
  hud.flash('Golden hour')
}

async function toggleAudio() {
  if (!audio.enabled) {
    await audio.start()
    hud.flash('Sound on')
  } else {
    audio.setEnabled(false)
    hud.flash('Sound off')
  }
}

/**
 * Proximity is measured from the orbit target (or the carried-flight position):
 * the place under attention, so a high explore orbit does not light every beacon.
 */
function scanProximity() {
  const canScan =
    !arrival &&
    !trail.active &&
    !activeEncounter &&
    !dialogue.isOpen() &&
    (controls.mode === 'explore' || controls.mode === 'flight')
  const focus = controls.getFocusPoint()
  let next = canScan
    ? poi.nearest(focus, camera.position.distanceTo(focus))
    : null
  // Top-down over town often fails the camera-distance POI gate; keep walk-in
  // alive from the look target alone.
  if (canScan && focusNearLaFortuna()) {
    next =
      poi.entryById('la-fortuna') ??
      poi.entryById('settlement-la-fortuna') ??
      next
  }
  const walkable = Boolean(
    next && (LA_FORTUNA_WALK_IDS.has(next.id) || focusNearLaFortuna()),
  )
  const prompt = next
    ? walkable
      ? `${next.name} · walk into the park (click or G)`
      : next.name
    : null
  if ((next?.id ?? null) !== (nearestPoi?.id ?? null)) {
    nearestPoi = next
    poi.setActive(next?.id ?? null)
  }
  hud.setPoiPrompt(prompt)
}

interface PendingPhoto {
  subject: string
  caption: string
  discoverableId?: string
}
let pendingPhoto: PendingPhoto | null = null

function kindForDiscoverable(id: string): DiscoverableKind {
  if (worldContent.attractions.some((entry) => entry.id === id)) return 'attraction'
  if (worldContent.settlements.some((entry) => entry.id === id)) return 'settlement'
  if (worldContent.history.some((entry) => entry.id === id)) return 'history'
  return 'hotspot'
}

function takePhoto(subject?: string, prompt?: string, discoverableId?: string) {
  if (pendingPhoto) return
  pendingPhoto = {
    subject: subject ?? currentView?.label ?? place.name,
    caption: prompt ?? currentJourney.title,
    discoverableId,
  }
  hud.shutter()
  audio.playCue('react')
}

let persistenceRequested = false

async function storeCompendiumPhoto(
  card: HTMLCanvasElement,
  id: string,
  subject: string,
  caption: string,
) {
  const [full, thumb] = await Promise.all([canvasToBlob(card), renderThumbnail(card)])
  if (!full || !thumb) {
    hud.flash('Could not save photo')
    return
  }
  const outcome = await putPhoto({
    id,
    placeId: place.id,
    subject,
    caption,
    takenAt: new Date().toISOString(),
    width: card.width,
    height: card.height,
    bytes: full.size,
    full,
    thumb: thumb.blob,
  })
  if (outcome === 'quota') hud.flash('Storage full — photo not saved')
  else if (outcome === 'failed') hud.flash('Could not save photo')
  else if (outcome === 'stored' && !persistenceRequested) {
    persistenceRequested = true
    void requestPersistence()
  }
}

function subjectForDiscoverable(id: string): string {
  return (
    worldContent.attractions.find((entry) => entry.id === id)?.name ??
    worldContent.settlements.find((entry) => entry.id === id)?.name ??
    worldContent.history.find((entry) => entry.id === id)?.title ??
    hotspots.find((entry) => entry.id === id)?.title ??
    id
  )
}

async function migrateLegacyThumbs(thumbs: LegacyThumb[]) {
  for (const entry of thumbs) {
    try {
      const blob = await fetch(entry.dataUrl).then((response) => response.blob())
      const bitmap = await createImageBitmap(blob)
      const outcome = await putPhoto({
        id: entry.id,
        placeId: place.id,
        subject: subjectForDiscoverable(entry.id),
        caption: place.name,
        takenAt: getRecord(questState, entry.id)?.photographedAt ?? new Date().toISOString(),
        width: bitmap.width,
        height: bitmap.height,
        bytes: blob.size,
        full: blob,
        thumb: blob,
      })
      bitmap.close()
      if (outcome === 'stored' && !persistenceRequested) {
        persistenceRequested = true
        void requestPersistence()
      }
    } catch {
      // Best-effort migration; the ledger flag stays so the player can retake.
    }
  }
}

/**
 * Runs inside the render loop: the drawing buffer is only readable in the same
 * tick as the draw, so the postcard is composed before the browser composites.
 */
function flushPendingPhoto() {
  const request = pendingPhoto
  if (!request) return
  pendingPhoto = null
  const card = composePostcard({
    source: renderer.domElement,
    placeName: place.name,
    subject: request.subject,
    caption: request.caption,
  })
  if (request.discoverableId) {
    const id = request.discoverableId
    const kind = kindForDiscoverable(id)
    markSeen(questState, id, kind)
    markPhotographed(questState, id, kind)
    saveQuestState(questState)
    hud.setProgress(discoveredCount(questState), totalDiscoverables)
    track('compendium_filled', { id, kind })
    syncQuests()
    void storeCompendiumPhoto(card, id, request.subject, request.caption)
  }
  track('photo_taken', {
    subject: request.subject,
    playerId: currentPlayer.id,
    journeyId: currentJourney.id,
  })
  void sharePostcard(
    card,
    `play-costa-rica-${slugify(request.subject)}.jpg`,
    `${place.name} — ${request.subject}`,
  ).then((outcome) => {
    if (outcome === 'shared') hud.flash('Photo shared')
    else if (outcome === 'downloaded') hud.flash('Photo saved')
    else hud.flash('Could not save the photo')
  })
}

async function shareCurrentView() {
  const view =
    currentView ??
    viewpoints.find((v) => v.id === place.signatureViewpointId) ??
    viewpoints[0]
  if (!view) return
  writeDeepLink({
    viewId: view.id,
    mode: controls.mode,
    playerId: currentPlayer.id,
    journeyId: currentJourney.id,
  })
  const url = shareViewpointUrl(view)
  try {
    if (navigator.share) {
      await navigator.share({ title: `Play Costa Rica — ${currentJourney.title}`, url })
      track('plan_shared', { playerId: currentPlayer.id, journeyId: currentJourney.id })
    } else if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url)
      track('plan_shared', { playerId: currentPlayer.id, journeyId: currentJourney.id })
      hud.flash('Link copied')
    } else {
      hud.flash(url)
    }
  } catch {
    hud.flash('Share canceled')
  }
}

function bootstrapFromUrl() {
  const shared = Boolean(initialLink.viewId)
  const view = shared
    ? viewpoints.find((v) => v.id === initialLink.viewId)
    : viewpoints.find((v) => v.id === currentJourney.arrivalViewpointId) ??
      viewpoints.find((v) => v.id === 'arenal-overview')
  // Stamping a default view into the URL makes the app's own boot pose
  // indistinguishable from a shared link on the next refresh, which would send
  // a first-time player to the map and skip the opening entirely.
  if (view) applyViewpoint(view, { link: shared })
  else if (viewpoints[0]) applyViewpoint(viewpoints[0], { link: false })

  if (initialLink.mode === 'trail') {
    hud.flash('Guided trails are retired — open the Atlas')
  } else if (initialLink.mode === 'walk') {
    trail.stop()
    controls.enabled = true
    controls.setMode('walk')
    writeDeepLink({ viewId: currentView?.id, mode: 'walk' })
  } else if (initialLink.mode === 'flight') {
    // A shared flight link cannot hand out the traversal; earning it is the reward.
    const unlocked = isTraversalUnlocked(worldContent.quests, questState, 'toucan-flight')
    if (unlocked) {
      trail.stop()
      controls.enabled = true
      controls.setMode('flight')
      audio.playCue('arrival')
    } else {
      hud.flash('Complete “The hill that woke” to fly with a guide bird')
    }
    writeDeepLink({ viewId: currentView?.id, mode: unlocked ? 'flight' : 'explore' })
  }
}

track('world_load', {
  hotspots: hotspots.length,
  journeys: journeys.length,
  playerId: hasPlaySelection ? currentPlayer.id : undefined,
})

hud.setProgress(discoveredCount(questState), totalDiscoverables)
hud.setJourney('Explorer', 'Arenal')
syncQuests()
/**
 * Boot on the region world map (orbit pan / explore). The title-sphere
 * interstitial and auto-drop into La Fortuna are retired — walk into town
 * from the map (G near La Fortuna) when you want the pocket.
 */
const legacyThumbs = takeLegacyThumbs(questState)
if (legacyThumbs.length > 0) {
  saveQuestState(questState)
  void migrateLegacyThumbs(legacyThumbs)
}

const clock = new THREE.Clock()
let poiScanAccum = 0
const raycaster = new THREE.Raycaster()
const pointer = new THREE.Vector2()
const pointerStart = new THREE.Vector2()
let pointerDragged = false

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
  postFx?.setSize(window.innerWidth, window.innerHeight)
  zoneHost.resize(window.innerWidth, window.innerHeight)
})

canvas.addEventListener('pointerdown', (event) => {
  if (event.button !== 0) return
  pointerStart.set(event.clientX, event.clientY)
  pointerDragged = false
})

canvas.addEventListener('pointermove', (event) => {
  if (event.buttons !== 1) return
  const dx = event.clientX - pointerStart.x
  const dy = event.clientY - pointerStart.y
  if (dx * dx + dy * dy > 25) pointerDragged = true
})

canvas.addEventListener('pointerup', (event) => {
  if (
    event.button !== 0 ||
    pointerDragged ||
    document.pointerLockElement === canvas
  ) {
    return
  }
  pointer.x = (event.clientX / window.innerWidth) * 2 - 1
  pointer.y = -(event.clientY / window.innerHeight) * 2 + 1
  raycaster.setFromCamera(pointer, camera)
  if (activeEncounter) {
    const hits = raycaster.intersectObjects(world.hotspotMeshes, false)
    const hotspotId = hits[0]?.object.userData.hotspotId as string | undefined
    if (hotspotId === activeEncounter.hotspot.id) {
      resolveActiveEncounter()
    } else {
      hud.flash(
        activeEncounter.stop.wrongHint ??
          `Keep looking for ${activeEncounter.hotspot.title}`,
      )
    }
    return
  }
  if (trail.active) return
  const beacons = raycaster.intersectObjects(poi.pickables, false)
  const poiId = beacons[0]?.object.userData.poiId as string | undefined
  if (poiId) {
    const entry = poi.entryById(poiId)
    if (entry) {
      openPoi(entry)
      return
    }
  }
  const reactions = raycaster.intersectObjects([...world.interactables, world.water], true)
  if (reactions[0]) {
    const id =
      (reactions[0].object.userData.interactionId as string | undefined) ??
      (reactions[0].object === world.water ? 'lake' : undefined)
    if (id) {
      const message = world.react(id, reactions[0].point)
      audio.playCue('react')
      hud.flash(message)
      track('world_reacted', { id, playerId: currentPlayer.id })
      return
    }
  }
  const hits = raycaster.intersectObjects(world.hotspotMeshes, false)
  if (hits[0]) {
    const id = hits[0].object.userData.hotspotId as string
    openHotspot(id)
  }
})

window.addEventListener('keydown', (event) => {
  if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return
  if (photoViewer.isOpen()) return
  const interact = event.key === 'e' || event.key === 'E'
  // Escape is layered: it closes what is on top of the world before it leaves
  // the world. Dismissing Don Rafa should not also eject you from the town.
  if (zoneHost.isActive() && !dialogue.isOpen() && !atlas.isOpen()) {
    if (event.key === 'Escape') {
      event.preventDefault()
      zoneHost.exit()
      return
    }
    if (interact) {
      const npc = zoneHost.nearbyNpc()
      if (npc) {
        event.preventDefault()
        talkToNpc(npc)
        return
      }
    }
    return
  }
  if (dialogue.isOpen()) {
    if (event.key === 'Escape') {
      dialogue.close()
      return
    }
    if (interact || event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      dialogue.advance()
    }
    return
  }
  if (event.key === 'Escape' && (roam.isOpen() || trail.active)) {
    event.preventDefault()
    exitToExplore('Guided trail closed')
    return
  }
  if (event.key === 'Escape' && controls.mode === 'flight') {
    event.preventDefault()
    controls.setMode('explore')
    writeDeepLink({
      viewId: currentView?.id,
      mode: 'explore',
      playerId: currentPlayer.id,
      journeyId: currentJourney.id,
    })
    return
  }
  // Air is the easiest thing to stop noticing and the easiest to over-tune, so
  // keep a reference frame one key away.
  if ((event.key === 'h' || event.key === 'H') && atmosphere) {
    event.preventDefault()
    atmosphere.setEnabled(!atmosphere.enabled)
    hud.flash(atmosphere.enabled ? 'Atmosphere on' : 'Atmosphere off')
    return
  }
  if (interact && nearestPoi) {
    event.preventDefault()
    openNearestPoi()
  }
  // Descending is proximity-gated: you walk into the town you are standing over,
  // not into whichever town you last read about.
  if (event.key === 'g' || event.key === 'G') {
    if (canEnterLaFortuna()) {
      event.preventDefault()
      enterLaFortunaTown()
      return
    }
    if (controls.mode === 'explore') {
      hud.flash('Pan onto La Fortuna, then press G (or Walk)')
    }
  }
})

canvas.addEventListener('dblclick', (event) => {
  if (controls.mode !== 'explore' || trail.active) return
  pointer.x = (event.clientX / window.innerWidth) * 2 - 1
  pointer.y = -(event.clientY / window.innerHeight) * 2 + 1
  raycaster.setFromCamera(pointer, camera)
  const hit = raycaster.intersectObjects([world.water, world.terrain], false)[0]
  if (hit) {
    controls.focusOn(hit.point)
    if (hit.object === world.water) {
      hud.flash(world.react('lake', hit.point))
      audio.playCue('react')
    }
  }
})

function frame() {
  const dt = Math.min(clock.getDelta(), 0.05)
  const elapsed = clock.elapsedTime

  // A ground zone owns the frame entirely: the region's terrain, POI beacons and
  // post-processing are all built for 90 m units and would be wrong at 1 m.
  if (zoneHost.isActive()) {
    dialogue.update(dt)
    zoneHost.update(dt, elapsed, dialogue.isOpen())
    zoneHost.render(dt)
    refreshNpcPrompt()
    // Must follow the render: the postcard is composed from the canvas.
    flushPendingPhoto()
    requestAnimationFrame(frame)
    return
  }

  // The loop starts before the region does, so that the zone has something
  // drawing it while the 44 km world is still being imported and built.
  if (!world) {
    dialogue.update(dt)
    requestAnimationFrame(frame)
    return
  }

  if (arrival) {
    arrival.elapsed += dt
    const progress = Math.min(1, arrival.elapsed / arrival.duration)
    const ease = 1 - Math.pow(1 - progress, 3)
    const cameraPosition = arrival.path.getPointAt(ease)
    const cameraTarget = arrival.startTarget.clone().lerp(arrival.target, ease)
    controls.setExplorePose(cameraPosition, cameraTarget)
    world.setArrivalProgress(ease)
    if (progress >= 1) {
      const complete = arrival.onComplete
      arrival = null
      controls.enabled = true
      hud.setTrailStatus('')
      complete()
    }
  } else if (trail.active) {
    trail.update(dt)
    hud.setTrailStatus(
      `Journey ${Math.min(trail.index + 1, trail.total)} / ${trail.total}`,
    )
  } else {
    controls.update(dt)
  }

  if (holdGolden > 0) {
    holdGolden -= dt
    timeOfDay = 0.22
  } else {
    timeOfDay = (timeOfDay + dt * 0.0035) % 1
  }
  world.setTimeOfDay(timeOfDay)
  world.update(elapsed)
  atmosphere.setSun(world.getSunDirection())
  atmosphere.update(elapsed)

  dialogue.update(dt)
  poiScanAccum += dt
  if (poiScanAccum >= 0.12) {
    poiScanAccum = 0
    scanProximity()
  }
  const focus = controls.getFocusPoint()
  poi.update(elapsed, focus, camera.position.distanceTo(focus))

  for (const mesh of world.hotspotMeshes) {
    const base = mesh.userData.baseY as number
    const phase = mesh.userData.phase as number
    const rhythm = Math.sin(elapsed * 1.7 + phase)
    const isTarget = activeEncounter?.hotspot.id === mesh.userData.hotspotId
    const hasTarget = Boolean(activeEncounter)
    const jump = isTarget ? Math.pow(Math.max(0, rhythm), 5) * 1.05 : 0
    mesh.position.y = base + jump + Math.sin(elapsed * 1.4 + phase) * 0.045
    const pulse = isTarget ? 1.28 + Math.max(0, rhythm) * 0.18 : hasTarget ? 0.68 : 1
    mesh.scale.setScalar(pulse)
    const material = mesh.material as THREE.MeshStandardMaterial
    material.transparent = hasTarget && !isTarget
    material.opacity = hasTarget && !isTarget ? 0.22 : 1
    material.emissiveIntensity = isTarget ? 1.35 : 0.78
    const halo = mesh.userData.halo as THREE.Mesh<
      THREE.TorusGeometry,
      THREE.MeshBasicMaterial
    >
    const haloPulse =
      1 + ((elapsed * (isTarget ? 0.9 : 0.35) + phase) % 1) * (isTarget ? 0.85 : 0.24)
    halo.scale.setScalar(haloPulse)
    halo.material.opacity = isTarget
      ? 0.9 - (haloPulse - 1) * 0.75
      : hasTarget
        ? 0.08
        : 0.4 - (haloPulse - 1) * 0.65
  }

  postFx?.setSun(world.sun.position)
  postFx.render(dt)
  flushPendingPhoto()
  requestAnimationFrame(frame)
}

if (import.meta.env.DEV) {
  // Leak checking is only meaningful as a measurement, so expose the counters.
  Object.assign(window as unknown as Record<string, unknown>, {
    // Air is tuned by eye, and by eye means isolating one term at a time.
    __atmos: {
      toggle: () => {
        atmosphere.setEnabled(!atmosphere.enabled)
        return atmosphere.enabled
      },
      tune: (values: Partial<AtmosphereSettings>) => atmosphere.tune(values),
      /** Cloud shadows alone, exaggerated. */
      cloudsOnly: () =>
        atmosphere.tune({ fogStrength: 0, cloudDepth: 0.6, cloudStrength: 1 }),
      /** Canopy sway alone, exaggerated. */
      windOnly: () =>
        atmosphere.tune({ fogStrength: 0, cloudStrength: 0, windStrength: 0.4 }),
    },
    __zone: {
      enter: () => zoneHost.enter('la-fortuna'),
      exit: () => zoneHost.exit(),
      audit: () => zoneHost.audit(),
      scene: () => zoneHost.current()?.scene,
      aim: () => zoneHost.aim(),
      nearbyNpc: () => zoneHost.nearbyNpc(),
      talkRafa: () => {
        // Always Don Rafa — opening test and debug; proximity optional.
        talkToNpc({
          id: 'don-rafa',
          name: 'Don Rafa',
          prompt: 'Talk to Don Rafa',
          assignsQuestId: 'the-lucky-town',
        })
      },
      /** Where you stand and which way you face, in terms the geo data uses. */
      where: () => {
        const zone = zoneHost.current()
        if (!zone) return null
        const forward = new THREE.Vector3()
        zone.camera.getWorldDirection(forward)
        // North is -Z in the zone frame, so a compass bearing is atan2(x, -z).
        const bearing =
          ((Math.atan2(forward.x, -forward.z) * 180) / Math.PI + 360) % 360
        const compass = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][
          Math.round(bearing / 45) % 8
        ]
        return { ...zone.position(), bearing: Math.round(bearing), compass }
      },
      warp: (lat: number, lon: number, bearing?: number) =>
        zoneHost.current()?.warp(lat, lon, bearing),
      faceVolcano: () => {
        const cone = worldContent.attractions.find(
          (entry) => entry.id === 'arenal-volcano',
        )
        if (cone) zoneHost.current()?.face(cone.location.lat, cone.location.lon)
      },
    },
  })
}

/**
 * Boot on the region map. The loop starts first so something can paint; the
 * 44 km world loads next and explore/pan takes over.
 */
frame()

// An async IIFE rather than top-level await: the build targets Safari 14, which
// predates top-level await, and dropping older iPhones to save one indentation
// level is a bad trade for a travel app.
void (async () => {
  arriving.hidden = true

  const { createArenalWorld } = await import('./world/createArenalWorld')
  // Both live in the region chunk already, so awaiting them here keeps the DEM
  // out of the entry bundle.
  const { WATER_Y } = await import('./world/terrain')
  const { createAtmosphere } = await import('./world/atmosphere')
  const { createCanopyLighting } = await import('./render/canopy')
  world = createArenalWorld(hotspots)
  world.initEnvironment(renderer)

  const vegetation = world.scene.getObjectByName('vegetation')
  const canopyMeshes = vegetation ? [vegetation] : []
  const canopyLighting = createCanopyLighting(renderer, {
    roofHeight: 0.28,
    roofDepth: 0.35,
    leafPeriod: 2.2,
    shade: 0.8,
    boost: 1.18,
  })
  atmosphere = createAtmosphere(world.scene, {
    fogBaseY: WATER_Y,
    shadowReceivers: [world.terrain],
    swaying: canopyMeshes,
    canopy: canopyLighting,
  })
  atmosphere.tune({ cloudMinY: 9, cloudScale: 0.014, cloudDepth: 0.3, cloudSpeed: 0.04 })
  atmosphere.setSun(world.getSunDirection())

  postFx = createPostFx(renderer, world.scene, camera, { outline: true })
  controls = createModeControls(camera, canvas, world.getHeight, world.scene)
  controls.enabled = !zoneHost.isActive()
  controls.onModeChange = (mode) => {
    hud.setMode(mode)
    hud.setHint(hintFor(mode))
  }
  // Attractions that already own an authored gold pin must not also get a beacon —
  // except La Fortuna, which is the walkable town and needs a discoverable pin.
  poi = createPointsOfInterest({
    attractions: worldContent.attractions,
    settlements: worldContent.settlements,
    getHeight: world.getHeight,
    excludeAttractionIds: new Set(
      [...hotspotAttractions.entries()]
        .filter(([hotspotId]) => hotspotById.has(hotspotId))
        .map(([, attraction]) => attraction.id)
        .filter((id) => id !== 'la-fortuna'),
    ),
  })
  world.scene.add(poi.group)
  // Built after the world, so it misses the initial material sweep.
  atmosphere.adopt(poi.group)

  bootstrapFromUrl()
  controls.enabled = true
  if (controls.mode !== 'explore' && controls.mode !== 'walk' && controls.mode !== 'flight') {
    controls.setMode('explore')
  }
  hud.setMode(controls.mode)
  hud.setHint(hintFor(controls.mode))
  // Tourism persona chooser is retired — boot straight into the world map.
  // Legacy journey JSON remains loadable for deep links until Campaign A replaces it.
})()
